import React, { Component } from "react";
import {
  Button,
  TextField,
  Autocomplete,
  IconButton,
  CircularProgress,
  Typography,
  Backdrop,
} from "@mui/material";
import moment from "moment";
import { withStyles } from "@mui/styles";
import { connect } from "react-redux";
import { DateRangePicker } from "../../components";
import { SectionAccordion, DocumentPdfDrawer } from "./components";
import agent from "../../agent/agent";
import { normalizeDocument } from "./helpers";
import { showError, showSuccess } from "../../utils/notifications";
import CloudDownloadIcon from "../../icons/CloudDownloadIcon";

const SECTIONS = [
  { key: "contracts", label: "Контракт", color: "#4F39F6", bg: "#EDE9FE" },
  { key: "addresses", label: "Адреса", color: "#CA8A04", bg: "#FEF9C3" },
  { key: "complexes", label: "Комплексы", color: "#16A34A", bg: "#DCFCE7" },
];

/** Порядок вкладок по типам (как в макете / админке). Пустой массив — без группировки по типам. */
const DOC_TYPE_ORDER = {
  contracts: [
    "Государственный контракт",
    "Сведения о передаче документации",
    "Дополнительное соглашение",
    "О начале оказания услуг",
    "БГ на участие в аукционе",
  ],
  addresses: [],
  complexes: [
    "Свидетельство о поверке",
    "Свидетельство об утверждении СИ",
    "Вывод из эксплуатации мобильных комплексов",
  ],
};

class Documents extends Component {
  constructor(props) {
    super(props);
    this.state = {
      documents: [],
      isLoading: true,
      filter: { name: "", type: "", dateStart: null, dateEnd: null },
      isBackdrop: false,
      pdfDrawerOpen: false,
      pdfDrawerLoading: false,
      pdfDocument: null,
      highlightDocumentId: null,
      expandedSectionKey: null,
      expandedDocTypeName: null,
    };
  }

  componentDidMount = async () => {
    const documents = await this.loadDocuments();
    await this.openPdfFromQuery(documents);
  };
  componentDidUpdate = async (prevProps) => {
    const prevId = prevProps.contract?.available_contracts?.contract_id;
    const currentId = this.props.contract?.available_contracts?.contract_id;
    if (prevId !== currentId) {
      const documents = await this.loadDocuments();
      await this.openPdfFromQuery(documents);
    }
  };

  clearOpenQueryParams = () => {
    const search = new URLSearchParams(window.location.search);
    ["open", "document_id", "url", "task_id"].forEach((key) => search.delete(key));
    const next = search.toString();
    const path = window.location.pathname;
    window.history.replaceState(null, "", next ? `${path}?${next}` : path);
  };

  openPdfDrawer = (document) => {
    if (!document?.url) {
      showError("Документ", "У задания нет файла PDF для просмотра");
      return;
    }
    this.setState({
      pdfDrawerOpen: true,
      pdfDrawerLoading: false,
      pdfDocument: document,
      highlightDocumentId: document?.id ? String(document.id) : null,
      expandedSectionKey: document?.section || "contracts",
      expandedDocTypeName: document?.type_name || null,
    });
  };

  closePdfDrawer = () => {
    this.setState({
      pdfDrawerOpen: false,
      pdfDrawerLoading: false,
      pdfDocument: null,
      highlightDocumentId: null,
    });
    this.clearOpenQueryParams();
  };

  fetchDocumentById = async (documentId, documentsList) => {
    if (!documentId) return null;
    const list = Array.isArray(documentsList) ? documentsList : this.state.documents;
    const fromList = list.find((doc) => String(doc.id) === String(documentId));
    if (fromList?.url) return fromList;

    const detail = await agent
      .get(`/documents/${documentId}`)
      .then((res) => {
        const payload = res?.data;
        const raw = payload?.data != null ? payload.data : payload;
        return raw && typeof raw === "object" ? normalizeDocument(raw) : null;
      })
      .catch(() => null);
    return detail;
  };

  openPdfFromQuery = async (documentsList) => {
    const search = new URLSearchParams(window.location.search);
    if (search.get("open") !== "1") return;

    const documentId = String(search.get("document_id") || "").trim();
    const url = String(search.get("url") || "").trim();
    const taskId = String(search.get("task_id") || "").trim();
    const documents = Array.isArray(documentsList) ? documentsList : this.state.documents;

    this.setState({
      pdfDrawerOpen: true,
      pdfDrawerLoading: true,
      pdfDocument: null,
      highlightDocumentId: null,
      expandedSectionKey: null,
      expandedDocTypeName: null,
    });

    let document = null;

    if (documentId) {
      document = await this.fetchDocumentById(documentId, documents);
    } else if (url) {
      document = {
        id: "",
        original_name: decodeURIComponent(url.split("/").pop() || "Документ задания"),
        url,
        format: "pdf",
      };
    } else if (taskId) {
      const task = await agent
        .get(`/tasks/${taskId}`)
        .then((res) => res?.data?.data ?? res?.data ?? null)
        .catch(() => null);
      const taskDocumentId = String(task?.document_id || "").trim();
      const taskPdfUrl = String(task?.url_pdf || task?.pdf_url || task?.document_url || "").trim();
      if (taskDocumentId) {
        document = await this.fetchDocumentById(taskDocumentId, documents);
      }
      if (!document?.url && taskPdfUrl) {
        document = {
          id: taskDocumentId,
          original_name: String(task?.name || task?.task_name || "Документ задания"),
          url: taskPdfUrl,
          format: "pdf",
        };
      }
    }

    if (!document?.url) {
      this.setState({ pdfDrawerOpen: false, pdfDrawerLoading: false, pdfDocument: null });
      showError("Документ", "Не удалось открыть PDF задания");
      this.clearOpenQueryParams();
      return;
    }

    // Подсветка документа в таблице (если он присутствует в текущем списке)
    const normalizeText = (value) => String(value ?? "").trim().toLowerCase().replace(/\s+/g, " ");
    const isSameName = (a, b) => {
      const na = normalizeText(a);
      const nb = normalizeText(b);
      if (!na || !nb) return false;
      return na === nb || na.includes(nb) || nb.includes(na);
    };

    const matchedInList =
      (document?.id ? documents.find((d) => String(d.id) === String(document.id)) : null) ||
      (documentId ? documents.find((d) => String(d.id) === String(documentId)) : null) ||
      (document?.original_name ? documents.find((d) => isSameName(d.original_name, document.original_name)) : null);

    const highlightDocumentId = matchedInList?.id
      ? String(matchedInList.id)
      : document?.id
        ? String(document.id)
        : documentId || null;
    // Пустой section у контрактов в API — считаем contracts
    const expandedSectionKey = matchedInList
      ? (matchedInList.section || "contracts")
      : null;
    const expandedDocTypeName = matchedInList?.type_name || null;

    this.setState({
      pdfDrawerOpen: true,
      pdfDrawerLoading: false,
      pdfDocument: matchedInList || document,
      highlightDocumentId,
      expandedSectionKey,
      expandedDocTypeName,
    });
  };

  loadDocuments = async () => {
    this.setState({ isLoading: true });
    const contractId = this.props.contract?.available_contracts?.contract_id;
    if (!contractId) {
      await new Promise((resolve) => this.setState({ documents: [], isLoading: false }, resolve));
      return [];
    }
    const params = new URLSearchParams({
      linked_entity_id: contractId,
      hide_from_user: "false",
      is_user: "true",
    });
    const documents = await agent
      .get(`/documents?${params.toString()}`)
      .then((res) => (res?.data?.data || []).map(normalizeDocument))
      .catch(() => []);
    await new Promise((resolve) => this.setState({ documents, isLoading: false }, resolve));
    return documents;
  };

  getDocTypesForSection = (sectionDocs, sectionKey) => {
    const present = [...new Set(sectionDocs.map((d) => d.type_name).filter(Boolean))];
    const order = DOC_TYPE_ORDER[sectionKey];
    if (!order?.length) return present;
    const ordered = order.filter((t) => present.includes(t));
    const extra = present.filter((t) => !order.includes(t));
    return [...ordered, ...extra];
  };

  handleFilterChange = (key, value) => {
    this.setState((prev) => ({ filter: { ...prev.filter, [key]: value } }));
  };
  handleReset = () => {
    this.setState({ filter: { name: "", type: "", dateStart: null, dateEnd: null } });
  };
  handleDateChange = ({ start, end }) => {
    this.setState((prev) => ({ filter: { ...prev.filter, dateStart: start, dateEnd: end } }));
  };

  getFilteredDocs = () => {
    const { documents, filter } = this.state;
    const parseDate = (value) => {
      if (!value) return null;
      const parsedStrict = moment(value, [
        moment.ISO_8601,
        "YYYY-MM-DD HH:mm:ss",
        "YYYY-MM-DD",
        "DD.MM.YYYY",
      ], true);
      if (parsedStrict.isValid()) return parsedStrict;
      const parsedLoose = moment(value);
      return parsedLoose.isValid() ? parsedLoose : null;
    };

    const filterStart = parseDate(filter.dateStart);
    const filterEnd = parseDate(filter.dateEnd);

    return documents.filter((doc) => {
      if (filter.name && !doc.original_name.toLowerCase().includes(filter.name.toLowerCase())) return false;
      if (filter.type && doc.type_name !== filter.type) return false;
      if (filterStart || filterEnd) {
        const docDate = parseDate(doc.created);
        if (!docDate) return false;
        if (filterStart && docDate.isBefore(filterStart)) return false;
        if (filterEnd && docDate.isAfter(filterEnd)) return false;
      }
      return true;
    });
  };

  getDocumentTypeIdByName = (typeName) => {
    if (!typeName) return null;
    const match = this.state.documents.find((doc) => doc.type_name === typeName && doc.document_type_id);
    return match?.document_type_id || null;
  };

  exportDocuments = async ({ section = null, documentTypeName = null } = {}) => {
    const contractId = this.props.contract?.available_contracts?.contract_id;
    if (!contractId) return;

    const { filter } = this.state;
    const selectedTypeName = documentTypeName || filter.type || "";
    const selectedTypeId = this.getDocumentTypeIdByName(selectedTypeName);

    const payload = {
      contract_id: contractId,
      type: "user_documents",
    };

    if (section) payload.section = section;
    if (selectedTypeId) payload.document_type_id = selectedTypeId;

    if (filter.name) payload.original_name = filter.name;
    if (filter.dateStart) payload.datetime_start = filter.dateStart;
    if (filter.dateEnd) payload.datetime_end = filter.dateEnd;

    this.setState({ isBackdrop: true });
    const result = await agent
      .post("/export/jobs", payload)
      .then((res) => res?.data)
      .catch((err) => ({ error: err?.response?.data || err }));
    this.setState({ isBackdrop: false });

    if (result?.error) {
      showError("Ошибка экспорта", result?.error?.details || "Не удалось поставить экспорт в очередь");
      return;
    }

    showSuccess("Экспорт документов", "Выгрузка поставлена в очередь");
  };

  render() {
    const { classes } = this.props;
    const { isLoading, filter, isBackdrop, pdfDrawerOpen, pdfDrawerLoading, pdfDocument, highlightDocumentId, expandedSectionKey, expandedDocTypeName } = this.state;
    const filtered = this.getFilteredDocs();
    const totalCount = filtered.length;
    const allTypes = [...new Set(this.state.documents.map((d) => d.type_name).filter(Boolean))];
    const expandedSectionKeySafe = expandedSectionKey && SECTIONS.some((s) => s.key === expandedSectionKey) ? expandedSectionKey : null;

    return (
      <div className={classes.root}>
        <div className={classes.header}>
          <div className={classes.headerLeft}>
            <h1 className={classes.title}>Документы</h1>
            {totalCount > 0 && <span className={classes.badge}>{totalCount.toLocaleString("ru-RU")}</span>}
            <IconButton onClick={this.loadDocuments} size="small" className={classes.refreshBtn}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 3.125 13.5 3.125 13.5M3.125 13.5H6.25M3.125 13.5V17M2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.8438 2.5 17.5 6.5 17.5 6.5M17.5 6.5V3M17.5 6.5H14.375" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </IconButton>
          </div>
          <Button variant="outlined" onClick={() => this.exportDocuments()} startIcon={
            <CloudDownloadIcon />
          } className={classes.downloadAllBtn}>
            Экспортировать
          </Button>
        </div>

        <div className={classes.filters}>
          <div className={classes.filterItem}>
            <TextField value={filter.name} onChange={(e) => this.handleFilterChange("name", e.target.value)} label="Название" variant="outlined" fullWidth className={`${classes.input} --floating-label`} />
          </div>
          <div className={classes.filterItem}>
            <Autocomplete value={allTypes.find((t) => t === filter.type) || null} options={allTypes} fullWidth renderInput={(params) => <TextField {...params} label="Тип документа" className={`${classes.input} --floating-label`} />} onChange={(e, v) => this.handleFilterChange("type", v || "")} />
          </div>
          <div className={classes.filterItem}>
            <DateRangePicker label="Дата загрузки" className="--floating-label" endDate={filter.dateEnd} startDate={filter.dateStart} onChange={this.handleDateChange} sx={{ "& .MuiOutlinedInput-root": { height: 44 } }} />
          </div>
          <Button color="secondary" variant="contained" onClick={this.handleReset} className={classes.btnAction}>Сбросить</Button>
          <Button variant="contained" className={classes.btnAction}>Применить</Button>
        </div>

        {isLoading ? (
          <div className={classes.loading}><CircularProgress /></div>
        ) : filtered.length === 0 ? (
          <Typography className={classes.empty}>Документы не найдены</Typography>
        ) : (
          <div className={classes.sections}>
            {SECTIONS.map((section) => {
              const sectionDocs = filtered.filter((d) => d.section === section.key);
              return (
                <SectionAccordion
                  key={section.key}
                  section={section}
                  documents={sectionDocs}
                  docTypes={this.getDocTypesForSection(sectionDocs, section.key)}
                  defaultOpen={expandedSectionKeySafe === section.key}
                  expandedDocTypeName={expandedDocTypeName}
                  highlightDocumentId={highlightDocumentId}
                  onExportSection={(sectionKey) => this.exportDocuments({ section: sectionKey })}
                  onExportDocType={(sectionKey, typeName) => this.exportDocuments({ section: sectionKey, documentTypeName: typeName })}
                />
              );
            })}
          </div>
        )}
        <Backdrop open={isBackdrop} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
          <CircularProgress />
        </Backdrop>
        <DocumentPdfDrawer
          open={pdfDrawerOpen}
          loading={pdfDrawerLoading}
          document={pdfDocument}
          onClose={this.closePdfDrawer}
        />
      </div>
    );
  }
}

const styles = {
  root: { display: "flex", flexDirection: "column", gap: 20 },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  headerLeft: { display: "flex", alignItems: "center", gap: 12 },
  title: { margin: 0, fontSize: 24, fontWeight: 500, lineHeight: "28px", color: "#111111" },
  badge: { fontSize: 12, fontWeight: 500, color: "#432DD7", backgroundColor: "#E0E7FF", borderRadius: 16, padding: "2px 8px" },
  refreshBtn: { color: "#4F39F6" },
  downloadAllBtn: {
    borderColor: "#E4E4E7", color: "#27272A", fontSize: 14, fontWeight: 500, height: 44, textTransform: "none",
    "&:hover": { borderColor: "#4F39F6", color: "#4F39F6", backgroundColor: "transparent" },
  },
  filters: { display: "flex", alignItems: "center", gap: 8 },
  filterItem: { flex: 1, minWidth: 0 },
  input: { "& .MuiOutlinedInput-root": { height: 44 } },
  btnAction: { flexShrink: 0, height: 44, fontSize: 14, fontWeight: 500, textTransform: "none", whiteSpace: "nowrap" },
  loading: { display: "flex", justifyContent: "center", padding: "64px 0" },
  empty: { fontSize: 14, color: "#71717A", textAlign: "center", padding: "48px 0" },
  sections: { display: "flex", flexDirection: "column", gap: 16 },
};

Documents = withStyles(styles)(Documents);
const mapStateToProps = (state) => ({ contract: state.contracts?.contract || null });
export default connect(mapStateToProps)(Documents);
